<template>
  <v-btn-toggle
    class="pa-1 rounded-lg"
    :model-value="modelValue"
    :class="wrapperClasses"
    v-bind="toggleProps"
    @update:model-value="handleUpdate"
  >
    <v-btn
      v-for="(item, index) in items"
      :key="`button-toggle-${index}`"
      :value="item.value"
      :to="item.to"
      :disabled="disabled || item.disabled"
      :ripple="false"
      :aria-label="item.label || item.tooltip"
      class="toggle-button rounded px-4"
    >
      <v-tooltip v-if="item.tooltip" activator="parent" location="top">
        {{ item.tooltip }}
      </v-tooltip>

      <v-icon
        v-if="item.icon"
        :icon="item.icon"
        :size="item.iconSize || 24"
        :class="{ 'mr-2': item.label }"
      />

      <span v-if="item.label" class="text-primary" :class="getLabelClasses(item.value)">
        {{ item.label }}
      </span>

      <v-icon
        v-if="item.iconAfterLabel"
        :icon="item.iconAfterLabel"
        class="ml-1"
        v-tooltip:top="item.iconAfterLabelTooltip"
      />
    </v-btn>
  </v-btn-toggle>
</template>

<script setup lang="ts" generic="T extends ButtonToggleValue">
import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";

export type ButtonToggleValue = string | number | boolean;

export interface ButtonToggleItem<T extends ButtonToggleValue = ButtonToggleValue> {
  value: T;
  label?: string;
  icon?: string;
  iconSize?: number;
  iconAfterLabel?: string;
  iconAfterLabelTooltip?: string;
  to?: RouteLocationRaw;
  tooltip?: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: T;
    items: ButtonToggleItem<T>[];
    density?: "default" | "comfortable" | "compact";
    disabled?: boolean;
    bgColor?: "indigo-pale" | "indigo-faint";
  }>(),
  {
    density: undefined,
    disabled: false,
    bgColor: "indigo-pale",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: T): void;
}>();

const wrapperClasses = computed(() => `button-toggle-wrapper bg-${props.bgColor}`);

const toggleProps = computed(() => ({
  mandatory: true,
  borderless: true,
  density: props.density,
  variant: "flat" as const,
  color: "white",
  rounded: true,
  baseColor: props.bgColor,
  selectedClass: "bg-white opacity-100",
}));

const getLabelClasses = (itemValue: T) => {
  return itemValue === props.modelValue ? "subtitle2 letter-spacing-normal" : "body2";
};

const handleUpdate = (value: T) => {
  emit("update:modelValue", value);
};
</script>

<style scoped lang="scss">
:deep(.v-btn.toggle-button) {
  --v-activated-opacity: 0;
  flex: 1 1 0;
  transition: all 0.2s ease-in-out;

  .v-btn__content {
    color: rgb(var(--v-theme-primary));
    text-transform: capitalize !important;
  }
}
</style>
